import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useMeStore } from '@/stores/me';

const routes: RouteRecordRaw[] = [
    { path: '/',         redirect: '/dashboard' },
    { path: '/login',    name: 'login',     component: () => import('@/views/Login.vue'),     meta: { public: true } },

    { path: '/dashboard', name: 'dashboard', component: () => import('@/views/Dashboard.vue') },
    { path: '/me',        name: 'profile',   component: () => import('@/views/Profile.vue') },

    { path: '/pages',                       name: 'pages',         component: () => import('@/views/PagesIndex.vue') },
    { path: '/pages/new',                   name: 'page-new',      component: () => import('@/views/PageEdit.vue'), props: { isNew: true } },
    { path: '/pages/:id(\\d+)',             name: 'page-edit',     component: () => import('@/views/PageEdit.vue'), props: true },
    { path: '/pages/:id(\\d+)/edit-inline', name: 'page-edit-inline', component: () => import('@/views/PageEditInline.vue'), props: true },

    { path: '/media',                       name: 'media',         component: () => import('@/views/MediaIndex.vue') },
    { path: '/media/:id(\\d+)',             name: 'media-edit',    component: () => import('@/views/MediaEdit.vue'), props: true },

    { path: '/portfolio',                   name: 'portfolio',     component: () => import('@/views/PortfolioIndex.vue') },

    // FAQ module — editor-facing, like Articles: writing answers is
    // editorial work. Categories and questions share one screen, so
    // there is a single route.
    { path: '/faq',                         name: 'faq',           component: () => import('@/views/FaqIndex.vue'), meta: { module: 'faq' } },

    { path: '/menus',                       name: 'menus',         component: () => import('@/views/MenusIndex.vue') },
    // Editor-facing, next to Menus: the people who rename and remove
    // pages are the ones who point the old addresses somewhere.
    { path: '/redirects',                   name: 'redirects',     component: () => import('@/views/RedirectsIndex.vue') },

    { path: '/form-submissions',            name: 'form-submissions',  component: () => import('@/views/FormSubmissionsIndex.vue') },
    { path: '/form-submissions/:id(\\d+)', name: 'form-submission',   component: () => import('@/views/FormSubmissionDetail.vue'), props: true },

    // Catalog module — gated by `meta.module: 'catalog'` (router guard
    // bounces non-enabled visits to the dashboard). Sidebar already hides
    // these entries when the module is off; this is the belt to the
    // sidebar's suspenders for direct-URL access.
    { path: '/products',                    name: 'products',      component: () => import('@/views/ProductsIndex.vue'),  meta: { module: 'catalog' } },
    { path: '/products/new',                name: 'product-new',   component: () => import('@/views/ProductEdit.vue'),    props: { isNew: true }, meta: { module: 'catalog' } },
    { path: '/products/:id(\\d+)',          name: 'product-edit',  component: () => import('@/views/ProductEdit.vue'),    props: true,            meta: { module: 'catalog' } },
    { path: '/products-import',             name: 'product-import',component: () => import('@/views/ProductImport.vue'),  meta: { module: 'catalog' } },
    { path: '/categories',                  name: 'categories',    component: () => import('@/views/CategoriesIndex.vue'),meta: { module: 'catalog' } },
    { path: '/categories/new',              name: 'category-new',  component: () => import('@/views/CategoryEdit.vue'),   props: { isNew: true }, meta: { module: 'catalog' } },
    { path: '/categories/:id(\\d+)',        name: 'category-edit', component: () => import('@/views/CategoryEdit.vue'),   props: true,            meta: { module: 'catalog' } },

    // Blog module — gated by `meta.module: 'blog'`. Editor-facing
    // (no meta.admin): writing articles is editorial work, same level
    // as managing Pages.
    { path: '/articles',                    name: 'articles',      component: () => import('@/views/ArticlesIndex.vue'), meta: { module: 'blog' } },
    { path: '/articles/new',                name: 'article-new',   component: () => import('@/views/ArticleEdit.vue'),   props: { isNew: true }, meta: { module: 'blog' } },
    { path: '/articles/:id(\\d+)',          name: 'article-edit',  component: () => import('@/views/ArticleEdit.vue'),   props: true,            meta: { module: 'blog' } },

    // Vehicle stock module: a car dealer's stock and the platforms it is
    // published to. Editor-facing, like Products: keeping the stock up to
    // date is the dealer's daily work.
    { path: '/vehicle-stock',               name: 'vehicle-stock', component: () => import('@/views/VehicleStockIndex.vue'), meta: { module: 'vehicle_stock' } },
    { path: '/vehicle-stock/new',           name: 'vehicle-new',   component: () => import('@/views/VehicleStockEdit.vue'),  props: { isNew: true }, meta: { module: 'vehicle_stock' } },
    { path: '/vehicle-stock/:id(\\d+)',     name: 'vehicle-edit',  component: () => import('@/views/VehicleStockEdit.vue'),  props: true,            meta: { module: 'vehicle_stock' } },

    // Customer module — admin-or-better, gated on the customer module.
    { path: '/customers',                   name: 'customers',     component: () => import('@/views/CustomersIndex.vue'), meta: { module: 'customer' } },
    { path: '/customers/:id(\\d+)',         name: 'customer-edit', component: () => import('@/views/CustomerEdit.vue'),   props: true, meta: { module: 'customer' } },
    // Customer groups — the sets that gate restricted pages, articles,
    // products and menu entries. Same gate as the customer list.
    { path: '/customer-groups',             name: 'customer-groups', component: () => import('@/views/CustomerGroupsIndex.vue'), meta: { module: 'customer' } },

    // Checkout module — admin-or-better. Refund + fulfillment are
    // back-office operations, never editor-level.
    { path: '/orders',                      name: 'orders',        component: () => import('@/views/OrdersIndex.vue'),    meta: { module: 'checkout' } },
    { path: '/orders/:id([0-9a-f-]{36})',   name: 'order-edit',    component: () => import('@/views/OrderEdit.vue'),      props: true, meta: { module: 'checkout' } },
    // Holiday closure: editor-level, unlike the rest of the checkout
    // settings, so a shop owner can close their own shop for the holidays.
    { path: '/holiday-closure',             name: 'holiday-closure', component: () => import('@/views/HolidayClosure.vue'), meta: { module: 'checkout' } },
    // Delivery slot module: the windows a shopper picks a delivery moment
    // from. Editor-level like the holiday closure, scoped to the active
    // website by the API.
    { path: '/delivery-slots',              name: 'delivery-slots', component: () => import('@/views/DeliverySlotSettings.vue'), meta: { module: 'delivery_slot' } },

    // Discount module — admin-or-better. Voucher codes are a back-office
    // pricing lever, scoped to the active website by the API.
    { path: '/discounts',                   name: 'discounts',     component: () => import('@/views/DiscountsIndex.vue'), meta: { module: 'discount' } },

    // Catalog-discount module — admin-or-better. Automatic sale + volume
    // pricing on products/categories (no code), scoped to the active website.
    { path: '/product-options',              name: 'product-options', component: () => import('@/views/ProductOptionsIndex.vue'), meta: { module: 'catalog' } },
    { path: '/gift-cards',                  name: 'gift-cards',    component: () => import('@/views/GiftCardsIndex.vue'), meta: { module: 'giftcard' } },
    { path: '/catalog-discounts',           name: 'catalog-discounts', component: () => import('@/views/CatalogDiscountsIndex.vue'), meta: { module: 'catalog_discount' } },

    // Shipping module — admin-or-better. Carriers, rate tables, zones and
    // pickup, scoped to the active website.
    { path: '/shipping',                     name: 'shipping',          component: () => import('@/views/ShippingIndex.vue'), meta: { module: 'shipping' } },

    // Newsletter module — admin-or-better. Subscriber list + bulk email
    // campaigns + per-website settings, scoped to the active website by
    // the API.
    { path: '/newsletter/subscribers',      name: 'newsletter-subscribers', component: () => import('@/views/NewsletterSubscribersIndex.vue'), meta: { module: 'newsletter' } },
    { path: '/newsletter/campaigns',        name: 'newsletter-campaigns',   component: () => import('@/views/NewsletterCampaignsIndex.vue'),   meta: { module: 'newsletter' } },
    { path: '/newsletter/campaigns/new',    name: 'newsletter-campaign-new',component: () => import('@/views/NewsletterCampaignEdit.vue'),     props: { isNew: true }, meta: { module: 'newsletter' } },
    { path: '/newsletter/campaigns/:id(\\d+)', name: 'newsletter-campaign-edit', component: () => import('@/views/NewsletterCampaignEdit.vue'),  props: true, meta: { module: 'newsletter' } },
    { path: '/newsletter/settings',         name: 'newsletter-settings',    component: () => import('@/views/NewsletterSettings.vue'),         meta: { module: 'newsletter' } },

    // Review requests module. One settings screen, scoped to the active
    // website by the API.
    { path: '/review-requests',             name: 'review-requests',        component: () => import('@/views/ReviewRequestSettings.vue'),      meta: { module: 'review_request' } },

    // Abandoned cart emails module. One settings screen, scoped to the
    // active website by the API.
    { path: '/abandoned-carts',             name: 'abandoned-carts',        component: () => import('@/views/AbandonedCartSettings.vue'),      meta: { module: 'abandoned_cart' } },

    // Landing-pages module — admin-or-better. Bulk operational
    // surface for programmatic SEO; not editor-facing.
    { path: '/landing',                              name: 'landing',          component: () => import('@/views/LandingIndex.vue'),       meta: { module: 'landingpages' } },
    { path: '/landing/new',                          name: 'landing-new',      component: () => import('@/views/LandingDatasetEdit.vue'), props: { isNew: true }, meta: { module: 'landingpages' } },
    { path: '/landing/:id(\\d+)',                    name: 'landing-edit',     component: () => import('@/views/LandingDatasetEdit.vue'), props: true,           meta: { module: 'landingpages' } },

    // VAT engine. The shop's VAT profile is its fiscal facts: admin-or-
    // better, on shops with the tax module. The tax table is the same law
    // for every shop, so super only.
    { path: '/vat',                         name: 'tax',           component: () => import('@/views/TaxSettings.vue'), meta: { module: 'tax', admin: true } },
    // SnelStart module: issued invoices booked in the shop's SnelStart
    // administration. Admin-only: it touches the shop's books.
    { path: '/snelstart',                   name: 'snelstart',     component: () => import('@/views/SnelstartIndex.vue'), meta: { module: 'snelstart', admin: true } },
    { path: '/tax-table',                   name: 'tax-table',     component: () => import('@/views/TaxTable.vue'), meta: { super: true } },

    // Websites: list + edit are admin-or-better (admins manage their own
    // sites). Creating a new website remains super-only — adding tenants
    // is a platform concern, not a tenant concern.
    { path: '/websites',                    name: 'websites',      component: () => import('@/views/WebsitesIndex.vue'), meta: { admin: true } },
    { path: '/websites/new',                name: 'website-new',   component: () => import('@/views/WebsiteEdit.vue'), props: { isNew: true }, meta: { super: true } },
    { path: '/websites/:id(\\d+)',          name: 'website-edit',  component: () => import('@/views/WebsiteEdit.vue'), props: true, meta: { admin: true } },

    // Statistics — admin-or-better. Per-website analytics dashboards
    // fed by the analytics_daily_* rollups. Editors don't see this;
    // the data crosses pages they don't manage.
    { path: '/statistics',           name: 'statistics',           component: () => import('@/views/StatisticsOverview.vue') },
    { path: '/statistics/pages',     name: 'statistics-pages',     component: () => import('@/views/StatisticsPages.vue') },
    { path: '/statistics/referrers', name: 'statistics-referrers', component: () => import('@/views/StatisticsReferrers.vue') },
    { path: '/statistics/geo',       name: 'statistics-geo',       component: () => import('@/views/StatisticsGeo.vue') },
    { path: '/statistics/devices',   name: 'statistics-technology', component: () => import('@/views/StatisticsTechnology.vue') },
    { path: '/statistics/campaigns', name: 'statistics-campaigns', component: () => import('@/views/StatisticsCampaigns.vue') },
    { path: '/statistics/conversions', name: 'statistics-conversions', component: () => import('@/views/StatisticsConversions.vue') },
    { path: '/statistics/errors',    name: 'statistics-errors',    component: () => import('@/views/StatisticsErrors.vue') },

    // Users: admin-or-better. Admins can only manage editors on their
    // own websites; the controllers enforce; the SPA's role/website
    // pickers narrow the form options accordingly.
    { path: '/users',                       name: 'users',         component: () => import('@/views/UsersIndex.vue'), meta: { admin: true } },
    { path: '/users/new',                   name: 'user-new',      component: () => import('@/views/UserEdit.vue'), props: { isNew: true }, meta: { admin: true } },
    { path: '/users/:id(\\d+)',             name: 'user-edit',     component: () => import('@/views/UserEdit.vue'), props: true, meta: { admin: true } },

    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFound.vue') },
];

export const router = createRouter({
    history: createWebHistory(),
    routes,
});

// Stale-SPA recovery: after a redeploy the loaded index.js references
// chunk filenames whose hashes no longer exist on disk, so every lazy
// route import 404s and the app hangs. Detect that failure and reload
// once to fetch the fresh index + chunk manifest. Guarded via
// sessionStorage so a genuinely broken build doesn't reload-loop.
router.onError((err, to) => {
    const message = err instanceof Error ? err.message : String(err);
    const isChunkLoadFailure =
        /Failed to fetch dynamically imported module/i.test(message)
        || /Importing a module script failed/i.test(message)
        || /error loading dynamically imported module/i.test(message);
    if (!isChunkLoadFailure) {
        return;
    }
    const reloadedKey = 'cms-admin.chunk-reload';
    if (sessionStorage.getItem(reloadedKey)) {
        return;
    }
    sessionStorage.setItem(reloadedKey, '1');
    window.location.assign(to.fullPath);
});

// Clear the reload guard once a navigation succeeds, so a future
// stale-chunk event in the same tab can recover again.
router.afterEach(() => {
    sessionStorage.removeItem('cms-admin.chunk-reload');
});

router.beforeEach(async (to) => {
    const me = useMeStore();
    if (!me.loaded) {
        await me.load();
    }
    if (!to.meta.public && !me.isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (to.name === 'login' && me.isAuthenticated) {
        return { name: 'dashboard' };
    }
    // Super-only routes: bounce non-supers to the dashboard.
    if (to.meta.super && !me.isSuper) {
        return { name: 'dashboard' };
    }
    // Admin-or-better routes (Websites, Users). isAdmin is true for
    // ROLE_ADMIN and ROLE_SUPER_ADMIN.
    if (to.meta.admin && !me.isAdmin) {
        return { name: 'dashboard' };
    }
    // Module-gated routes: bounce when the module is not enabled on the
    // active website. Sidebar already hides these entries; this guard
    // covers direct-URL access (links shared, bookmarks).
    if (typeof to.meta.module === 'string' && !me.isModuleEnabled(to.meta.module)) {
        return { name: 'dashboard' };
    }
    // Fresh-cluster bootstrap: a super admin with no active website (no
    // website exists yet) gets routed to the new-website form. Other
    // routes (Dashboard, Pages, Media, …) all need an active website
    // and would 4xx without one. Allow the websites list and the new
    // form so super admins can navigate freely once they're there.
    if (
        me.isAuthenticated && me.isSuper
        && me.me?.activeWebsite === null
        && to.name !== 'website-new'
        && to.name !== 'websites'
    ) {
        return { name: 'website-new' };
    }
    return true;
});
